import React from "react";
import { useAuth } from "../context/AuthContext.tsx";
import ProjectList from "./ProjectList.tsx";
import { useTranslation } from "react-i18next";
import { Project, ProjectStatus } from "../types";

interface ProjectListSectionProps {
    projects: Project[];
    isLoading: boolean;
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
    onPageChange: (newPage: number) => void;
    onUpdateProjectStatus: (projectId: string, newStatus: ProjectStatus) => Promise<void>;
}

const ProjectListSection: React.FC<ProjectListSectionProps> = ({ 
    projects,
    isLoading,
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    onPageChange,
    onUpdateProjectStatus,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
  
    if (isLoading) {
        return <div className="text-center py-8">{t('dashboard.loading')}</div>;
    }
  
    return (
        <div className="bg-white rounded-lg shadow">
            <div className="p-4">
                <h2 className="text-lg font-semibold mb-4">{t('dashboard.projectsSection.title')}</h2>
                {projects.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        {t('dashboard.projectsSection.noProjects')}
                    </div>
                ) : (
                    <>
                        <ProjectList 
                            projects={projects} 
                            currentUser={user ?? { id: '', email: '', name: '', role: '', permissions: [], isActive: false }}
                            onUpdateProjectStatus={onUpdateProjectStatus}
                        />
              
                        {/* Pagination Controls */}
                        <div className="mt-4 flex justify-between items-center">
                            <div className="text-sm text-gray-500">
                                {t('dashboard.projectsSection.showing', {
                                    from: ((currentPage - 1) * pageSize) + 1,
                                    to: Math.min(currentPage * pageSize, totalCount),
                                    total: totalCount
                                })}
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => onPageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 border rounded disabled:opacity-50"
                                >
                                    {t('dashboard.pagination.previous')}
                                </button>
                                <span className="px-3 py-1">
                                    {t('dashboard.pagination.pageInfo', { current: currentPage, total: totalPages })}
                                </span>
                                <button
                                    onClick={() => onPageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 border rounded disabled:opacity-50"
                                >
                                    {t('dashboard.pagination.next')}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ProjectListSection;
